import { useTheme } from '@emotion/react';
import { Delete, Edit } from '@mui/icons-material';
import {
    Box,
    CircularProgress,
    Dialog,
    DialogContent,
    Grid,
    IconButton,
    InputAdornment,
    Paper,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TextField,
    Typography,
    useMediaQuery,
} from '@mui/material';
import React, { memo, useCallback, useContext, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { toast } from 'sonner';
import { Context } from '../../../App';
import CommonDialog from '../../../common/CommonDialog';
import { MuiTextFieldWithFocus40 } from '../../../config/MaterialUIConstants';
import { addInvoiceFeesApi, deleteInvoiceFeesApi, getInvoiceFeesListApi, updateInvoiceFeesApi } from '../../../redux/Invoice/action';
import { formatCurrency } from '../../../utility/helpers/displayHelper';
import SuccessMessageModal from '../../Common/SuccessMessage';
import FeesModal from '../Pricing/FeesModal';

const CONTAINER_STYLES = {
    boxShadow: 3,
    bgcolor: 'white',
    borderRadius: '10px',
    marginTop: '32px',
    padding: '24px',
};

const ACTION_BUTTON_STYLES = {
    fontSize: '14px !important',
    letterSpacing: '0.4px',
};

const PartRelatedFees = ({ type = 'invoice' }) => {
    const dispatch = useDispatch();
    const theme = useTheme();
    const isMobileView = useMediaQuery(theme.breakpoints.down('md'));
    const { isOpenSideBar } = useContext(Context);

    const [isFeesModalOpen, setIsFeesModalOpen] = useState(false);
    const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
    const [isSuccessModalOpen, setIsSuccessModalOpen] = useState(false);
    const [editingFeeIndex, setEditingFeeIndex] = useState(-1);
    const [feeToDeleteId, setFeeToDeleteId] = useState(null);
    const [successMessage, setSuccessMessage] = useState('');
    const [isDeleteInProgress, setIsDeleteInProgress] = useState(false);
    const [isPageLoading, setIsPageLoading] = useState(false);
    const [isFeeOperationInProgress, setIsFeeOperationInProgress] = useState(false);

    const feesList = useSelector((state) => state?.invoice?.feesList?.data) || [];

    const fetchFeesList = useCallback(async () => {
        try {
            setIsPageLoading(true);
            await dispatch(getInvoiceFeesListApi({ pagination: false, type: type }));
        } catch (error) {
            setSuccessMessage('Error fetching fees data');
            setIsSuccessModalOpen(true);
            console.error('Error fetching fees:', error);
        } finally {
            setIsPageLoading(false);
        }
    }, [dispatch, type]);

    useEffect(() => {
        fetchFeesList();
    }, [fetchFeesList]);

    const handleFeeOperation = useCallback(
        async (operationType, feeData) => {
            try {
                setIsFeeOperationInProgress(true);
                setIsPageLoading(true);
                let response;

                if (operationType === 'add') {
                    response = await addInvoiceFeesApi(feeData);
                    setSuccessMessage('Fee added successfully');
                } else {
                    const feeId = feesList[editingFeeIndex]?._id;
                    response = await updateInvoiceFeesApi(feeId, feeData);
                    setSuccessMessage('Fee updated successfully');
                }

                if (response) {
                    setEditingFeeIndex(-1);
                    await fetchFeesList();
                    setIsFeesModalOpen(false);
                    setIsSuccessModalOpen(true);
                }
            } catch (error) {
                toast.error('Operation failed. Please try again.');
                console.error('Error in fee operation:', error);
            } finally {
                setIsFeeOperationInProgress(false);
                setIsPageLoading(false);
            }
        },
        [editingFeeIndex, feesList, fetchFeesList]
    );

    const handleDeleteFee = useCallback(async () => {
        if (!feeToDeleteId) return;

        try {
            setIsDeleteInProgress(true);
            const response = await deleteInvoiceFeesApi(feeToDeleteId);

            if (response) {
                setIsDeleteModalOpen(false);
                setFeeToDeleteId(null);
                await fetchFeesList();
                setSuccessMessage('Fee deleted successfully');
                setIsSuccessModalOpen(true);
            }
        } catch (error) {
            setSuccessMessage('Failed to delete fee. Please try again.');
            setIsSuccessModalOpen(true);
            console.error('Error deleting fee:', error);
        } finally {
            setIsDeleteInProgress(false);
        }
    }, [feeToDeleteId, fetchFeesList]);

    const confirmDeleteFee = useCallback((fee) => {
        if (fee?._id) {
            setFeeToDeleteId(fee._id);
            setIsDeleteModalOpen(true);
        }
    }, []);

    const openEditFeeModal = useCallback((index) => {
        setEditingFeeIndex(index);
        setIsFeesModalOpen(true);
    }, []);

    const toggleDefaultStatus = useCallback(
        async (fee) => {
            try {
                setIsPageLoading(true);
                const updatedFee = {
                    name: fee.name,
                    calculationType: type === 'invoice' ? 'percentage' : 'fixed_amount',
                    percentage: fee.percentage,
                    type: fee.type,
                    isDefault: !fee.isDefault,
                };

                const response = await updateInvoiceFeesApi(fee._id, updatedFee);

                if (response) {
                    await fetchFeesList();
                }
            } catch (error) {
                setSuccessMessage('Failed to update default status');
                setIsSuccessModalOpen(true);
                console.error('Error updating default status:', error);
            } finally {
                setIsPageLoading(false);
            }
        },
        [fetchFeesList, type]
    );

    const formatPercentage = useCallback((value) => {
        const numValue = typeof value === 'number' ? value : Number(value);
        return isNaN(numValue) ? '0.00' : numValue.toFixed(2);
    }, []);

    const handleCloseFeesModal = useCallback(() => {
        setIsFeesModalOpen(false);
        if (editingFeeIndex !== -1) setEditingFeeIndex(-1);
    }, [editingFeeIndex]);

    const handleCloseDeleteModal = useCallback(() => {
        if (!isDeleteInProgress) {
            setIsDeleteModalOpen(false);
            setFeeToDeleteId(null);
        }
    }, [isDeleteInProgress]);

    return (
        <div>
            <Box>
                <Grid container sx={CONTAINER_STYLES} justifyContent="space-between">
                    <Grid item xs={12} md={4}>
                        <Typography variant="h5" sx={{ fontWeight: 400, fontSize: '24px' }}>
                            Part Related Fees
                        </Typography>
                    </Grid>

                    <Grid item xs={12} md={8}>
                        <Grid container item xs={12} justifyContent="end" style={{ marginTop: '24px' }}>
                            {/* <Grid container item lg={12} justifyContent="end">
                                <Button
                                    variant="contained"
                                    color="secondary"
                                    size="large"
                                    style={{ marginBottom: '24px' }}
                                    onClick={() => setIsFeesModalOpen(true)}
                                >
                                    ADD NEW FEES
                                </Button>
                            </Grid> */}

                            {Array.isArray(feesList) && feesList.length > 0 ? (
                                <Grid item xs={12}>
                                    <TableContainer
                                        component={Paper}
                                        style={{
                                            boxShadow: 'none',
                                            overflowX: 'inherit',
                                            border: '1px solid var(--box-stroke-10, rgba(0, 0, 0, 0.10))',
                                            background: 'var(--PureWhite, #FFF)',
                                            boxShadow: '0px 2px 3px 0px rgba(0, 0, 0, 0.06)',
                                            padding: '12px',
                                        }}
                                    >
                                        <Table size="small">
                                            <TableHead>
                                                <TableRow style={{ backgroundColor: '#F8F8F8' }}>
                                                    <TableCell style={{ border: 'none', fontWeight: '600', width: '75%', padding: '8px 16px' }}>Name</TableCell>
                                                    <TableCell style={{ border: 'none', fontWeight: '600', width: '15%', padding: '8px 16px' }}>
                                                        Amount
                                                    </TableCell>
                                                    <TableCell style={{ border: 'none', fontWeight: '600', padding: '8px 16px' }}>Actions</TableCell>
                                                </TableRow>
                                            </TableHead>
                                            <TableBody>
                                                {feesList.map((fee, index) => (
                                                    <TableRow key={`fee-${fee._id || index}`}>
                                                        <TableCell style={{ border: 'none', fontSize: '16px', fontWeight: '500', padding: '16px 16px' }}>
                                                            {fee.name}
                                                        </TableCell>

                                                        <TableCell style={{ border: 'none', padding: '16px 16px' }}>
                                                            <TextField
                                                                value={formatCurrency(fee.amount)}
                                                                disabled
                                                                sx={MuiTextFieldWithFocus40}
                                                                endAdornment={<InputAdornment position="end">%</InputAdornment>}
                                                            />
                                                        </TableCell>

                                                        <TableCell align="right" style={{ border: 'none', padding: '16px 16px' }}>
                                                            <Stack spacing={1} direction="row" justifyContent="flex-end">
                                                                <IconButton
                                                                    size="small"
                                                                    onClick={() => {
                                                                        openEditFeeModal(index);
                                                                    }}
                                                                >
                                                                    <Edit
                                                                        style={{
                                                                            color: '#646466',
                                                                        }}
                                                                    />
                                                                </IconButton>
                                                                <IconButton
                                                                    size="small"
                                                                    onClick={() => {
                                                                        confirmDeleteFee(fee);
                                                                    }}
                                                                >
                                                                    <Delete color="error" />
                                                                </IconButton>
                                                            </Stack>
                                                        </TableCell>
                                                    </TableRow>
                                                ))}
                                            </TableBody>
                                        </Table>
                                    </TableContainer>
                                </Grid>
                            ) : (
                                !isPageLoading && (
                                    <Typography variant="body1" align="center" width="100%" mt={2}>
                                        No fees have been added yet.
                                    </Typography>
                                )
                            )}
                        </Grid>
                    </Grid>
                </Grid>
            </Box>

            <FeesModal
                type={type}
                loader={isFeeOperationInProgress}
                toolbarBg="white"
                color="#000000"
                width="500px"
                showBoxShadow={false}
                open={isFeesModalOpen}
                onClose={handleCloseFeesModal}
                onSave={handleFeeOperation}
                updateIndex={editingFeeIndex}
                setUpdateIndex={setEditingFeeIndex}
                discountProgram={feesList}
            />

            <CommonDialog
                loader={isDeleteInProgress}
                open={isDeleteModalOpen}
                onClose={handleCloseDeleteModal}
                onSave={handleDeleteFee}
                dialogTitle="Delete Confirmation"
                dialogSubtitle="Are you sure you want to delete this fee?"
            />

            <Dialog fullScreen={isMobileView} open={isPageLoading} aria-labelledby="loading-dialog">
                <DialogContent>
                    <CircularProgress />
                </DialogContent>
            </Dialog>

            <SuccessMessageModal message={successMessage} open={isSuccessModalOpen} setOpenSuccessModal={setIsSuccessModalOpen} />
        </div>
    );
};

export default memo(PartRelatedFees);
